/**
 * Estado de cuenta por producto del asociado — impreso institucional.
 *
 * Trae estado_cuenta_asociado(asociado) + config_reporte (membrete legal) y
 * abre una ventana con el HTML listo para imprimir / guardar PDF. Mismo patrón
 * de ventana que imprimibles.ts / imprimirCdp.ts.
 */
import { supabase } from './supabase'
import { fetchConfigReporte } from './useConfigReporte'

type Result = { ok: true } | { ok: false; error: string }

const fmtQ = (n: unknown) =>
  'Q ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}
const fmtFechaHora = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
const esc = (s: unknown) =>
  String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))

type Mov = { fecha: string; descripcion: string; monto: number; saldo: number }
type EstadoCuenta = {
  asociado: { no_asociado: string; nombre_completo: string; dpi: string | null }
  generado_at: string
  ahorro: { numero_cuenta: string | null; saldo: number; movimientos: Mov[] }
  aportaciones: { saldo: number; movimientos: Mov[] }
  plazos_fijos: Array<{ cuenta_id: string; capital_inicial: number; tasa_anual: number; plazo_dias: number; fecha_inicio: string; fecha_vencimiento: string }>
  creditos: Array<{ codigo: string; estado: string; capital_inicial: number; saldo_capital: number; tasa: number; total_cuotas: number; fecha_desembolso: string | null; pagos: Array<{ fecha: string; monto_total: number; capital: number; interes: number; mora: number }> }>
}

function tablaMovimientos(movs: Mov[]): string {
  if (!movs.length) return `<p class="vacio">Sin movimientos registrados.</p>`
  const filas = movs.map((m) => {
    const esAbono = Number(m.monto) >= 0
    return `<tr>
      <td class="nums">${fmtFecha(m.fecha)}</td>
      <td>${esc(m.descripcion)}</td>
      <td class="nums ${esAbono ? 'abono' : 'cargo'}">${esAbono ? '' : '-'}${fmtQ(Math.abs(Number(m.monto)))}</td>
      <td class="nums saldo">${fmtQ(m.saldo)}</td>
    </tr>`
  }).join('')
  return `<table class="mov">
    <thead><tr><th>Fecha</th><th>Descripción</th><th class="nums">Cargo / Abono</th><th class="nums">Saldo</th></tr></thead>
    <tbody>${filas}</tbody>
  </table>`
}

function seccionProducto(titulo: string, meta: string, saldoLabel: string, saldo: number, cuerpo: string): string {
  return `<section class="producto">
    <div class="prod-head">
      <div><h2>${esc(titulo)}</h2><div class="prod-meta">${meta}</div></div>
      <div class="prod-saldo"><span class="eyebrow">${esc(saldoLabel)}</span><strong>${fmtQ(saldo)}</strong></div>
    </div>
    ${cuerpo}
  </section>`
}

export async function imprimirEstadoCuentaAsociado(asociadoId: string): Promise<Result> {
  const [{ data, error }, cfg] = await Promise.all([
    supabase.rpc('estado_cuenta_asociado', { p_asociado_id: asociadoId }),
    fetchConfigReporte(),
  ])
  if (error) return { ok: false, error: error.message }
  if (!data) return { ok: false, error: 'No se pudo generar el estado de cuenta' }
  const ec = data as EstadoCuenta

  // ── Secciones (solo las que tienen contenido) ──────────────────────────────
  const secciones: string[] = []

  if (ec.ahorro && (ec.ahorro.movimientos.length > 0 || Number(ec.ahorro.saldo) !== 0)) {
    secciones.push(seccionProducto(
      'Ahorro corriente',
      ec.ahorro.numero_cuenta ? `Cuenta ${esc(ec.ahorro.numero_cuenta)}` : 'Cuenta de ahorro',
      'Saldo disponible', Number(ec.ahorro.saldo), tablaMovimientos(ec.ahorro.movimientos),
    ))
  }
  if (ec.aportaciones && (ec.aportaciones.movimientos.length > 0 || Number(ec.aportaciones.saldo) !== 0)) {
    secciones.push(seccionProducto(
      'Aportaciones', 'Capital social', 'Saldo acumulado',
      Number(ec.aportaciones.saldo), tablaMovimientos(ec.aportaciones.movimientos),
    ))
  }
  if (ec.plazos_fijos && ec.plazos_fijos.length) {
    const filas = ec.plazos_fijos.map((p) => `<tr>
      <td>${esc(p.cuenta_id)}</td>
      <td class="nums">${fmtQ(p.capital_inicial)}</td>
      <td class="nums">${(Number(p.tasa_anual) * 100).toFixed(2)}%</td>
      <td class="nums">${p.plazo_dias} días</td>
      <td class="nums">${fmtFecha(p.fecha_inicio)}</td>
      <td class="nums">${fmtFecha(p.fecha_vencimiento)}</td>
    </tr>`).join('')
    const totalPF = ec.plazos_fijos.reduce((s, p) => s + Number(p.capital_inicial), 0)
    secciones.push(seccionProducto('Plazos fijos', `${ec.plazos_fijos.length} certificado(s)`, 'Capital invertido', totalPF,
      `<table class="mov"><thead><tr><th>Certificado</th><th class="nums">Capital</th><th class="nums">Tasa</th><th class="nums">Plazo</th><th class="nums">Inicio</th><th class="nums">Vencimiento</th></tr></thead><tbody>${filas}</tbody></table>`))
  }
  if (ec.creditos && ec.creditos.length) {
    for (const c of ec.creditos) {
      const pagos = c.pagos.length
        ? `<table class="mov"><thead><tr><th>Fecha</th><th class="nums">Pago</th><th class="nums">Capital</th><th class="nums">Interés</th><th class="nums">Mora</th></tr></thead><tbody>${
            c.pagos.map((p) => `<tr><td class="nums">${fmtFecha(p.fecha)}</td><td class="nums">${fmtQ(p.monto_total)}</td><td class="nums">${fmtQ(p.capital)}</td><td class="nums">${fmtQ(p.interes)}</td><td class="nums ${Number(p.mora) > 0 ? 'cargo' : ''}">${fmtQ(p.mora)}</td></tr>`).join('')
          }</tbody></table>`
        : `<p class="vacio">Sin pagos registrados.</p>`
      secciones.push(seccionProducto(
        `Crédito ${esc(c.codigo)}`,
        `${esc(c.estado)} · ${fmtQ(c.capital_inicial)} a ${c.total_cuotas} cuota(s) · desembolsado ${fmtFecha(c.fecha_desembolso)}`,
        'Saldo capital', Number(c.saldo_capital), pagos,
      ))
    }
  }

  if (!secciones.length) secciones.push(`<p class="vacio" style="padding:40px 0;text-align:center">El asociado no tiene productos con movimientos.</p>`)

  const razon = esc(cfg?.razon_social ?? 'Cooperativa Integral de Ahorro y Crédito NEO, R.L.')
  const dir = esc(cfg?.direccion_fiscal ?? '')
  const nit = esc(cfg?.nit ?? '')
  const tel = esc(cfg?.telefono ?? '')
  const email = esc(cfg?.email ?? '')
  const pie = esc(cfg?.pie_legal ?? '')
  const logo = cfg?.logo_url || '/brand/isotipo-naranja.svg'
  const contador = esc(cfg?.perito_contador_nombre ?? '')
  const contadorNit = esc(cfg?.perito_contador_nit ?? '')
  const repLegal = esc(cfg?.representante_legal_nombre ?? '')

  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>Estado de cuenta · ${esc(ec.asociado.nombre_completo)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  :root { --brand:#FF5B04; --ink:#14171c; --soft:#4b5563; --muted:#9aa0a6; --line:#ebebed; --soft-gray:#f6f7f9; }
  body { font-family:'Noto Sans',system-ui,sans-serif; color:var(--ink); background:#e9eaec; font-size:12px; line-height:1.5; }
  .page { max-width:860px; margin:24px auto; background:#fff; padding:40px 44px; box-shadow:0 20px 50px rgba(0,0,0,.12); }
  header.doc { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid var(--brand); padding-bottom:16px; }
  .org { display:flex; gap:14px; align-items:center; }
  .org img { width:46px; height:46px; }
  .org h1 { font-size:15px; font-weight:800; color:var(--ink); line-height:1.2; max-width:420px; }
  .org .meta { font-size:10.5px; color:var(--soft); margin-top:3px; }
  .doc-title { text-align:right; }
  .doc-title .eyebrow { font-size:10px; font-weight:800; letter-spacing:1.5px; color:var(--brand); text-transform:uppercase; }
  .doc-title h2 { font-size:18px; font-weight:800; margin-top:2px; }
  .doc-title .gen { font-size:10.5px; color:var(--muted); margin-top:2px; }
  .asociado { display:flex; gap:28px; background:var(--soft-gray); border-radius:12px; padding:14px 18px; margin:18px 0 8px; }
  .asociado div span { display:block; font-size:9.5px; font-weight:800; letter-spacing:1px; color:var(--muted); text-transform:uppercase; }
  .asociado div strong { font-size:13px; font-weight:700; }
  section.producto { margin-top:22px; page-break-inside:avoid; }
  .prod-head { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:1px solid var(--line); padding-bottom:8px; margin-bottom:8px; }
  .prod-head h2 { font-size:14px; font-weight:800; }
  .prod-meta { font-size:10.5px; color:var(--soft); margin-top:2px; }
  .prod-saldo { text-align:right; }
  .prod-saldo .eyebrow { display:block; font-size:9px; font-weight:800; letter-spacing:1px; color:var(--muted); text-transform:uppercase; }
  .prod-saldo strong { font-size:16px; font-weight:800; color:var(--brand); }
  table.mov { width:100%; border-collapse:collapse; margin-top:4px; }
  table.mov th { font-size:9.5px; font-weight:800; letter-spacing:.5px; text-transform:uppercase; color:var(--muted); text-align:left; padding:6px 8px; border-bottom:1px solid var(--line); }
  table.mov td { font-size:11px; padding:6px 8px; border-bottom:1px solid var(--line); }
  .nums { text-align:right; font-variant-numeric:tabular-nums; }
  td.nums:first-child, th.nums:first-child { text-align:left; }
  .abono { color:#047857; } .cargo { color:#b91c1c; } .saldo { font-weight:700; }
  .vacio { font-size:11px; color:var(--muted); padding:8px 0; }
  .firmas { display:flex; gap:60px; justify-content:center; margin-top:48px; page-break-inside:avoid; }
  .firma { text-align:center; min-width:220px; }
  .firma .linea { border-top:1px solid var(--ink); margin-bottom:6px; }
  .firma strong { display:block; font-size:12px; font-weight:700; }
  .firma span { font-size:10px; color:var(--muted); }
  footer.doc { margin-top:28px; border-top:1px solid var(--line); padding-top:12px; font-size:9.5px; color:var(--muted); line-height:1.5; }
  @media print { body { background:#fff; } .page { box-shadow:none; margin:0; max-width:none; padding:18px 22px; } .noprint { display:none !important; } }
  .bar { position:fixed; top:12px; right:12px; display:flex; gap:8px; }
  .bar button { font-family:inherit; font-weight:700; font-size:12px; border:none; border-radius:8px; padding:9px 16px; cursor:pointer; }
  .bar .p { background:var(--brand); color:#fff; } .bar .c { background:#fff; color:var(--ink); border:1px solid var(--line); }
</style></head>
<body>
  <div class="bar noprint">
    <button class="p" onclick="window.print()">Imprimir / PDF</button>
    <button class="c" onclick="window.close()">Cerrar</button>
  </div>
  <div class="page">
    <header class="doc">
      <div class="org">
        <img src="${esc(logo)}" alt="">
        <div><h1>${razon}</h1><div class="meta">${nit ? 'NIT ' + nit : ''}${dir ? ' · ' + dir : ''}${tel ? ' · Tel ' + tel : ''}${email ? ' · ' + email : ''}</div></div>
      </div>
      <div class="doc-title">
        <div class="eyebrow">Estado de cuenta</div>
        <h2>Por producto</h2>
        <div class="gen">Generado ${fmtFechaHora(ec.generado_at)}</div>
      </div>
    </header>
    <div class="asociado">
      <div><span>Asociado</span><strong>${esc(ec.asociado.nombre_completo)}</strong></div>
      <div><span>No. asociado</span><strong>${esc(ec.asociado.no_asociado)}</strong></div>
      ${ec.asociado.dpi ? `<div><span>DPI</span><strong>${esc(ec.asociado.dpi)}</strong></div>` : ''}
    </div>
    ${secciones.join('')}
    ${(contador || repLegal) ? `<div class="firmas">
      ${contador ? `<div class="firma"><div class="linea"></div><strong>${contador}</strong><span>Perito Contador${contadorNit ? ' · NIT ' + contadorNit : ''}</span></div>` : ''}
      ${repLegal ? `<div class="firma"><div class="linea"></div><strong>${repLegal}</strong><span>Representante Legal</span></div>` : ''}
    </div>` : ''}
    <footer class="doc">
      ${pie ? pie + '<br>' : ''}
      Documento informativo generado por ${razon}. Los saldos reflejan los movimientos registrados a la fecha de emisión.
    </footer>
  </div>
</body></html>`

  const w = window.open('', '_blank', 'width=1100,height=860')
  if (!w) return { ok: false, error: 'El navegador bloqueó la ventana. Permití los popups.' }
  w.document.write(html)
  w.document.close()
  return { ok: true }
}
